import React, { useCallback } from 'react';
import { _ } from '@splunk/ui-utils/i18n';
import ChevronsOut from '@splunk/react-icons/ChevronsOut';
import {
    useFullscreenActionButtonRef,
    useFocusHistoryApi,
} from '@splunk/dashboard-context';
import {
    selectIsDashboardFullscreen,
    useSelector,
} from '@splunk/dashboard-state';
import { ActionButton, useActionButtonClick } from '../ActionButton';
import type { BaseButtonProps } from '../BaseButton';
import { RENDER_ACTION_BUTTON_DENYLIST } from '../utils/displayUtils';
import { getButtonTitle } from '../utils/getButtonTitle';
import { useItemName } from '../utils/useItemName';

const fullscreenIcon = <ChevronsOut />;

// I would have preferred to have BaseBttonProps include the disabled prop as we use it but OpenInSearch does it differently
type FullscreenButtonProps = BaseButtonProps & {
    disabled?: boolean;
};

export const FullscreenButton = ({
    itemId,
    itemDefinition,
    dashboardApi,
    screenReaderText,
    disabled,
    type,
    ...rest
}: FullscreenButtonProps): JSX.Element | null => {
    const vizName = useItemName({ type, itemDefinition });

    const buttonRef = useFullscreenActionButtonRef(itemId ?? '');
    const focusHistoryApi = useFocusHistoryApi();
    const isDashboardFullscreen = useSelector(selectIsDashboardFullscreen);

    const vizType = itemDefinition?.type ?? null;
    const toggleFullscreen = useCallback(async () => {
        // dashboardApi and itemId have to exist to execute this callback, using non-null assertion operator
        await dashboardApi!.toggleVisualizationFullscreen(
            itemId!,
            'actionMenu'
        );
        focusHistoryApi.setReturnFocusElement(buttonRef);
    }, [dashboardApi, itemId, focusHistoryApi, buttonRef]);

    const handleOnClick = useActionButtonClick('fullscreen', toggleFullscreen);

    if (
        !itemId ||
        !dashboardApi ||
        !vizType ||
        RENDER_ACTION_BUTTON_DENYLIST.includes(vizType)
    ) {
        return null;
    }

    const buttonTitle = getButtonTitle({
        base: _('Fullscreen'),
        vizName,
        screenReaderText,
    });

    return (
        <ActionButton
            {...rest}
            data-test="fullscreen-button"
            disabled={disabled ?? isDashboardFullscreen}
            onClick={
                (disabled ?? isDashboardFullscreen) ? undefined : handleOnClick
            }
            icon={fullscreenIcon}
            title={buttonTitle}
            elementRef={buttonRef}
        />
    );
};
